import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  CodeType,
  CreatePromotionCodeRequest,
  DiscountType,
  FeeType,
  PromotionCode,
  UpdatePromotionCodeRequest,
} from "../../../types/admin";

interface PromotionCodeModalProps {
  open: boolean;
  mode: "create" | "edit";
  initialData?: PromotionCode;
  onClose: () => void;
  onSave: (
    payload: CreatePromotionCodeRequest | UpdatePromotionCodeRequest,
  ) => Promise<void> | void;
  submitting?: boolean;
}

const PromotionCodeModal: React.FC<PromotionCodeModalProps> = ({
  open,
  mode,
  initialData,
  onClose,
  onSave,
  submitting,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState<string>(initialData?.code ?? "");
  const [description, setDescription] = useState<string>(
    initialData?.description ?? "",
  );
  const [discountType, setDiscountType] = useState<DiscountType>(
    initialData?.discountType ?? DiscountType.Percentage,
  );
  const [discountValue, setDiscountValue] = useState<number>(
    initialData?.discountValue ?? 0,
  );
  const [feeType, setFeeType] = useState<FeeType>(
    initialData?.feeType ?? FeeType.ServiceFee,
  );
  const [codeType, setCodeType] = useState<CodeType>(
    initialData?.codeType ?? CodeType.General,
  );
  const [isActive, setIsActive] = useState<boolean>(
    initialData?.isActive ?? true,
  );
  const [startDate, setStartDate] = useState<string>(
    initialData?.startDate
      ? new Date(initialData.startDate).toISOString().split("T")[0]
      : "",
  );
  const [endDate, setEndDate] = useState<string>(
    initialData?.endDate
      ? new Date(initialData.endDate).toISOString().split("T")[0]
      : "",
  );
  const [maxTotalUses, setMaxTotalUses] = useState<string>(
    initialData?.maxTotalUses?.toString() ?? "",
  );
  const [maxUsesPerUser, setMaxUsesPerUser] = useState<string>(
    initialData?.maxUsesPerUser?.toString() ?? "",
  );
  const [minOrderValue, setMinOrderValue] = useState<string>(
    initialData?.minOrderValue?.toString() ?? "",
  );
  const [routeRestrictions, setRouteRestrictions] = useState<string>(
    initialData?.routeRestrictions ?? "",
  );
  const [notes, setNotes] = useState<string>(initialData?.notes ?? "");

  useEffect(() => {
    if (open) {
      setCode(initialData?.code ?? "");
      setDescription(initialData?.description ?? "");
      setDiscountType(initialData?.discountType ?? DiscountType.Percentage);
      setDiscountValue(initialData?.discountValue ?? 0);
      setFeeType(initialData?.feeType ?? FeeType.ServiceFee);
      setCodeType(initialData?.codeType ?? CodeType.General);
      setIsActive(initialData?.isActive ?? true);
      setStartDate(
        initialData?.startDate
          ? new Date(initialData.startDate).toISOString().split("T")[0]
          : "",
      );
      setEndDate(
        initialData?.endDate
          ? new Date(initialData.endDate).toISOString().split("T")[0]
          : "",
      );
      setMaxTotalUses(initialData?.maxTotalUses?.toString() ?? "");
      setMaxUsesPerUser(initialData?.maxUsesPerUser?.toString() ?? "");
      setMinOrderValue(initialData?.minOrderValue?.toString() ?? "");
      setRouteRestrictions(initialData?.routeRestrictions ?? "");
      setNotes(initialData?.notes ?? "");
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    const payload: CreatePromotionCodeRequest | UpdatePromotionCodeRequest =
      mode === "create"
        ? {
            code: code.toUpperCase(),
            description,
            discountType,
            discountValue: Number(discountValue) || 0,
            feeType,
            codeType,
            isActive,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            maxTotalUses: maxTotalUses ? Number(maxTotalUses) : undefined,
            maxUsesPerUser: maxUsesPerUser ? Number(maxUsesPerUser) : undefined,
            minOrderValue: minOrderValue ? Number(minOrderValue) : undefined,
            routeRestrictions: routeRestrictions || undefined,
            notes: notes || undefined,
          }
        : {
            description,
            discountType,
            discountValue: Number(discountValue) || 0,
            feeType,
            codeType,
            isActive,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            maxTotalUses: maxTotalUses ? Number(maxTotalUses) : undefined,
            maxUsesPerUser: maxUsesPerUser ? Number(maxUsesPerUser) : undefined,
            minOrderValue: minOrderValue ? Number(minOrderValue) : undefined,
            routeRestrictions: routeRestrictions || undefined,
            notes: notes || undefined,
          };

    await onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {mode === "create" ? t("admin.createPromotionCode") : t("admin.editPromotionCode")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stack spacing={2}>
            {mode === "create" && (
              <TextField
                label={t("admin.code")}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                fullWidth
                required
                helperText={t("admin.uniqueCodeHelperText")}
                inputProps={{ style: { fontFamily: "monospace" } }}
              />
            )}
            <TextField
              label={t("admin.description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              required
              helperText={t("admin.descriptionOfCodeHelperText")}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t("admin.discountType")}</InputLabel>
                <Select
                  value={discountType}
                  onChange={(e) =>
                    setDiscountType(e.target.value as DiscountType)
                  }
                  label={t("admin.discountType")}
                >
                  <MenuItem value={DiscountType.Percentage}>{t("admin.percentage")}</MenuItem>
                  <MenuItem value={DiscountType.FixedAmount}>
                    {t("admin.fixedAmount")}
                  </MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={`${t("admin.discountValue")} ${discountType === DiscountType.Percentage ? "(%)" : "($)"}`}
                type="number"
                value={discountValue}
                onChange={(e) => setDiscountValue(Number(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                required
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>{t("admin.feeType")}</InputLabel>
              <Select
                value={feeType}
                onChange={(e) => setFeeType(e.target.value as FeeType)}
                label={t("admin.feeType")}
              >
                <MenuItem value={FeeType.ServiceFee}>{t("admin.serviceFee")}</MenuItem>
                <MenuItem value={FeeType.CargoFee}>{t("admin.cargoFee")}</MenuItem>
                <MenuItem value={FeeType.AllFees}>{t("admin.allFees")}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t("admin.codeType")}</InputLabel>
              <Select
                value={codeType}
                onChange={(e) => setCodeType(e.target.value as CodeType)}
                label={t("admin.codeType")}
              >
                <MenuItem value={CodeType.FirstTime}>{t("admin.firstTime")}</MenuItem>
                <MenuItem value={CodeType.Seasonal}>{t("admin.seasonal")}</MenuItem>
                <MenuItem value={CodeType.Loyalty}>{t("admin.loyalty")}</MenuItem>
                <MenuItem value={CodeType.Referral}>{t("admin.referral")}</MenuItem>
                <MenuItem value={CodeType.General}>{t("admin.general")}</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.startDate")}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={t("admin.endDate")}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("admin.maxTotalUses")}
                type="number"
                value={maxTotalUses}
                onChange={(e) => setMaxTotalUses(e.target.value)}
                inputProps={{ min: 1 }}
                fullWidth
                helperText={t("admin.leaveEmptyForUnlimited")}
              />
              <TextField
                label={t("admin.maxUsesPerUser")}
                type="number"
                value={maxUsesPerUser}
                onChange={(e) => setMaxUsesPerUser(e.target.value)}
                inputProps={{ min: 1 }}
                fullWidth
                helperText={t("admin.leaveEmptyForUnlimited")}
              />
            </Box>
            <TextField
              label={t("admin.minOrderValue")}
              type="number"
              value={minOrderValue}
              onChange={(e) => setMinOrderValue(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              helperText={t("admin.minOrderValueHelperText")}
            />
            <TextField
              label={t("admin.routeRestrictions")}
              value={routeRestrictions}
              onChange={(e) => setRouteRestrictions(e.target.value)}
              fullWidth
              helperText={t("admin.routeRestrictionsHelperText")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={t("common.active")}
            />
            <TextField
              label={t("admin.notes")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText={t("admin.optionalAdminNotes")}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !description || (mode === "create" && !code)}
        >
          {submitting ? t("common.saving") : mode === "create" ? t("common.create") : t("common.update")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionCodeModal;

